import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { IoCreateOutline, IoEyeOutline } from "react-icons/io5";
import { getNodeServiceBaseUrl, getSwaggerServiceBaseUrl } from "../../../../config/apiBase";
import ModulePage, { ModuleToolbar } from "../../../../shared-ui/ModulePage";
import { EmptyState, ErrorState, Skeleton } from "../../../../shared-ui/States";
import "../../../../shared-ui/crane-tokens.css";
import "../../../../shared-ui/RowActions.css";

function formatShortDate(value) {
  if (!value) return "—";
  const s = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

/** Drafted / Rejected invoices remain editable (matches Invoice module.js). */
function canEditInvoiceStatus(status) {
  const s = String(status || "").trim().toLowerCase();
  if (!s || s === "drafted" || s === "draft") return true;
  if (s.includes("rejected")) return true;
  return false;
}

export default function StitchInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const token = localStorage.getItem("zTaskAuthToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      const invoicesUrl = `${getNodeServiceBaseUrl()}/invoice/api/invoices`;
      const customersUrl = `${getNodeServiceBaseUrl()}/customer/api/customers/`;
      const projectsUrl = `${getSwaggerServiceBaseUrl()}/api/get-project`;

      const [invRes, custRes, projRes] = await Promise.all([
        fetch(invoicesUrl, { headers }),
        fetch(customersUrl, { headers }),
        fetch(projectsUrl, { headers }),
      ]);
      if (!invRes.ok) throw new Error("Failed to fetch invoices");
      const data = await invRes.json();
      const customers = custRes.ok ? await custRes.json() : [];
      const projectsPayload = projRes.ok ? await projRes.json() : { projects: [] };
      const projects = projectsPayload.projects || [];

      const customerMap = {};
      (Array.isArray(customers) ? customers : []).forEach((c) => {
        customerMap[c.id] = c.name;
        customerMap[String(c.id)] = c.name;
      });
      const projectMap = {};
      projects.forEach((p) => {
        const pid = p.projectId ?? p.id;
        const pname = p.projectName ?? p.name;
        projectMap[pid] = pname;
        projectMap[String(pid)] = pname;
      });

      const enriched = (Array.isArray(data) ? data : []).map((invoice) => ({
        ...invoice,
        client_name:
          customerMap[invoice.cust_id] || customerMap[String(invoice.cust_id)] || "",
        project_name:
          projectMap[invoice.site_id] || projectMap[String(invoice.site_id)] || "",
      }));
      enriched.sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
      setInvoices(enriched);
    } catch (e) {
      setErr(e.message || "Failed to load invoices");
      setInvoices([]);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((inv) => {
      const hay = [
        inv.doc_no,
        inv.id,
        inv.current_status,
        inv.client_name,
        inv.project_name,
        inv.inv_date,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [invoices, query]);

  return (
    <ModulePage
      crumbs={["Operations", "ZOrder", "Invoices"]}
      title="Invoices"
      description="Receivables list from the invoice service."
      actions={
        <>
          <button type="button" className="crane-btn" onClick={load} disabled={busy}>
            Refresh
          </button>
          <Link to="/invoices/new" className="crane-btn crane-btn--primary" style={{ textDecoration: "none" }}>
            + New invoice
          </Link>
        </>
      }
    >
      <ModuleToolbar>
        <input
          type="search"
          className="crane-input"
          placeholder="Search invoice #, customer, status…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter invoices"
          style={{ maxWidth: 360 }}
        />
      </ModuleToolbar>

      {busy && <Skeleton rows={6} height={40} />}
      {!busy && err && <ErrorState title="Could not load invoices" description={err} onRetry={load} />}
      {!busy && !err && filtered.length === 0 && (
        <EmptyState
          title={query ? "No matching invoices" : "No invoices yet"}
          description={query ? "Try another search." : "Invoices appear here once created in ZOrder."}
          action={
            !query ? (
              <Link to="/invoices/new" className="crane-btn crane-btn--primary" style={{ textDecoration: "none" }}>
                New invoice
              </Link>
            ) : null
          }
        />
      )}
      {!busy && !err && filtered.length > 0 && (
        <div className="crane-card" style={{ overflowX: "auto" }}>
          <table className="crane-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Invoice date</th>
                <th>Amount</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => {
                const editable = canEditInvoiceStatus(inv.current_status);
                return (
                  <tr key={inv.id}>
                    <td>
                      <Link to={`/invoices/${inv.id}`}>{inv.doc_no || inv.id}</Link>
                    </td>
                    <td>{inv.client_name || "—"}</td>
                    <td>{inv.current_status || "—"}</td>
                    <td>{formatShortDate(inv.inv_date)}</td>
                    <td>
                      {inv.amount != null && inv.amount !== ""
                        ? Number(inv.amount).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div className="crane-row-actions">
                        <NavLink
                          to={`/invoices/${inv.id}`}
                          className="crane-row-action crane-row-action--view"
                          title="View / Print"
                          aria-label="View invoice"
                        >
                          <IoEyeOutline size={16} />
                        </NavLink>
                        <NavLink
                          to={`/invoices/${inv.id}/edit`}
                          className="crane-row-action crane-row-action--edit"
                          title={editable ? "Edit" : "Open invoice (approval / status actions)"}
                          aria-label="Edit invoice"
                        >
                          <IoCreateOutline size={16} />
                        </NavLink>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ModulePage>
  );
}
